/**
 * TimelineData format adapter for RoughCutPlayer and Timeline widget
 *
 * Defines data conversion APIs between 3 major components
 * storyToPlayer - Story TimelineData into RoughCutPlayer play list
 * tuneToStory - Story TimelineData visualTracks into Timeline UI widget-ready visualTracks
 * widgetToStory - Timeline UI widget visualTracks into Story TimelineData
 */
import app from 'app';
import RCP from '@wochitnpm/rcp';
import _ from 'lodash-extended';
import fg from '#/libs/FrameGrid';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import TimelineUndoRedoController from '../views/Story/TimelineUndoRedoController';
import TextOverlayEffectController from './TextOverlayEffectController';
import storyViewBus from '../views/Story/storyViewBus';
import TimelineTranslateController from './TimelineTranslateController';
import { RatioChange_AutoCrop } from '@wochitnpm/adjustasset';
import { workPool } from '@wochitnpm/helpers';

class PlaylistGapDescriptor {
  constructor() {
    this.hasGaps = false;
    this.totalGapTime = 0;
    this.aGap = [];
    this.isTimelineEmpty = false;
  }
}

class GapItem {
  constructor(o) {
    this.displayStartTime = null;
    this.displayDuration = null;
    this.displayEndTime = null;
    this.index = null;

    // copy from `o` only those properties that declared in `this`
    for (const key in o) {
      // noinspection JSUnfilteredForInLoop
      if (Object.getOwnPropertyDescriptor(this, key)) {
        // noinspection JSUnfilteredForInLoop
        this[key] = o[key];
      }
    }
  }
}

export default class TimelineGatewayController {
  constructor() {
    // zendive.timelineRedesign _debouncedUpdateSequence
    this.serializeTimelineDebounced = _.debounce(
      () => {
        this.serializeTimeline(null, { pause: false });
      },
      app.TIME.MENTAL.λ,
      { leading: false, trailing: true }
    );
    this.whenIsTuned = new _.ColdPromise();
    this.whenWidgetReady = new _.ColdPromise();
  }

  /**
   * set instance variables to current story
   * @param story
   */
  tuneToStory(story) {
    this.story = story;
    this.volumeSettings = app.getVolumeSettings(story);
    this.programmer = story.getProgrammer();
    this.undoredo?.destroy();
    this.undoredo = new TimelineUndoRedoController(this.story);
    this.workPoolHandlers = new Set();

    workPool.on(
      {
        type: workPool.job.cropSocialAsset.type,
      },
      ({ asset }) => {
        const visualTrack = _.find(this.getVisualData(), { guid: asset.guid });
        workPool.job.cropSocialAsset.updateTimelineTrack({
          asset,
          visualTrack,
        });
      },
      this.workPoolHandlers
    );

    workPool.on(
      {
        type: workPool.job.trimVideoFragment.type,
      },
      (payload) => {
        const assets = this.getVisualData();
        const shouldSerialize =
          workPool.job.trimVideoFragment.updateTimelineTrack({
            asset: payload.asset,
            assets,
          });
        if (shouldSerialize) {
          this.serializeTimelineDebounced();
        }
      },
      this.workPoolHandlers
    );

    workPool.on(
      { type: workPool.job.trimVideoFragment.type },
      (payload) => {
        workPool.job.trimVideoFragment.updateSelectedAsset(
          payload.asset,
          app.rootStore.state.story.selectedAssets
        );
      },
      this.workPoolHandlers
    );

    workPool.on(
      { type: workPool.job.createExternalAsset.type },
      (payload) => {
        workPool.job.createExternalAsset.updateSelectedAsset(
          payload.asset,
          app.rootStore.state.story.selectedAssets
        );
        if (!payload.error) {
          workPool.job.createAssetPreview.forExternalAsset({
            asset: payload.asset,
            programmer: this.programmer.attributes,
          });
          workPool.job.trimVideoFragment.execute({ asset: payload.asset });
          app.storyController.story.adjustVideoPoster_onAssetSelected(
            payload.asset
          );
        }
      },
      this.workPoolHandlers
    );

    workPool.on(
      { type: workPool.job.createAssetPreview.type },
      (payload) => {
        workPool.job.createAssetPreview.updateSelectedAsset(
          payload.asset,
          app.rootStore.state.story.selectedAssets
        );
      },
      this.workPoolHandlers
    );

    app.on('app.asset.edited', this._assetMetadataChanged.bind(this));

    this.whenIsTuned.resolve();
  }

  destroy() {
    this.story = null;
    this.volumeSettings = null;
    this.programmer = null;
    this.undoredo.destroy();
    this.undoredo = null;
    workPool.off(this.workPoolHandlers);
    app.off('app.asset.edited');
  }

  getStoryVisualTracks() {
    const rv = [];
    const timelineData = this.story.getTimelineData();

    _.eachIn(timelineData, 'visualTracks', (asset) => {
      rv.push(asset);
    });

    return rv;
  }

  storyToPlayer(options) {
    options = $.extend(
      {
        fillGaps: true,
        ignoreRtpForAsset: null,
      },
      options
    );

    const timelineData = this.story.getTimelineData();
    const feed = new RCP.ns.PlayList.Feed();
    feed.totalDisplayTime = timelineData.totalDisplayTime;

    _.each(timelineData?.visualTracks, (track) => {
      const ignoreRtp = options.ignoreRtpForAsset
        ? track.displayStartTime === options.ignoreRtpForAsset.displayStartTime
        : false;
      feed.visual.push(
        TimelineGatewayController.createPlayerVisualTrack({
          asset: track,
          ignoreRtp,
        })
      );
    });

    _.each(app.rootStore.state.story.audioTracks, (audioTrack) => {
      const selectedSegment = _.find(
        app.rootStore.state.story.selectedVoiceOverSegments,
        (segment) => {
          return (
            segment.audioSegmentContent.asset.metadata.assetUUID ===
            audioTrack.assetUUID
          );
        }
      );

      if (selectedSegment) {
        const pAudio = TimelineGatewayController.createPlayerAudioTrack(
          selectedSegment.audioSegmentContent.cdnUrl,
          audioTrack
        );
        feed.audio.push(pAudio);
      }
    });

    if (options.fillGaps) {
      const gd = this.storyFindGaps(this.getStoryVisualTracks());

      if (gd.hasGaps) {
        const is9x16 = 'ASPECT_9_16' === this.story.get('aspectRatio');
        let outputPlayList = [];
        let playListCopy = feed.visual;
        let latestIndexSlices = 0;
        let gapTrack;
        let src;

        if (gd.isTimelineEmpty) {
          src = is9x16
            ? app.DEFAULT.EMPTY_TIMELINE_IMG_NARROW
            : app.DEFAULT.EMPTY_TIMELINE_IMG;
        } else {
          src = is9x16
            ? app.DEFAULT.MISSING_ASSET_IMG_NARROW
            : app.DEFAULT.MISSING_ASSET_IMG;
        }

        _.each(gd.aGap, (gapItem) => {
          gapTrack = new RCP.ns.PlayList.VisualTrackItem({
            type: 'image',
            volumeControl: {
              video: this.volumeSettings.novoSettings.mix.video,
              narration: this.volumeSettings.novoSettings.mix.narration,
              bgMusic: this.volumeSettings.novoSettings.mix.bg,
            },
            onVoPlayVolumeControl: {
              video: this.volumeSettings.narrationSettings.mix.video,
              narration: this.volumeSettings.narrationSettings.mix.narration,
              bgMusic: this.volumeSettings.narrationSettings.mix.bg,
            },
            src: src,
            play: {
              from: gapItem.displayStartTime,
              duration: gapItem.displayDuration,
              until: gapItem.displayEndTime,
            },
          });

          playListCopy = feed.visual;
          playListCopy = playListCopy.slice(latestIndexSlices, gapItem.index);
          outputPlayList = outputPlayList.concat(playListCopy, gapTrack);
          latestIndexSlices = gapItem.index;
        });

        if (latestIndexSlices < feed.visual.length) {
          playListCopy = feed.visual;
          playListCopy = playListCopy.slice(latestIndexSlices);
          outputPlayList = outputPlayList.concat(playListCopy);
        }

        feed.visual = outputPlayList;
      }
    }

    const bgMusic = this.createPlayerBGMusicTrack();
    if (!_.isEmpty(bgMusic)) {
      feed.bgMusic.push(bgMusic);
    }

    return feed;
  }

  static createPlayerAudioTrack(src, audioTrack) {
    return new RCP.ns.PlayList.AudioTrackItem({
      src: app.prependHttp(src),
      blob: true,
      play: {
        from: audioTrack.displayStartTime,
        duration: audioTrack.displayEndTime - audioTrack.displayStartTime,
        until: audioTrack.displayEndTime,
      },
      fragment: {
        from: audioTrack.relativeStartTime,
        duration: audioTrack.relativeEndTime - audioTrack.relativeStartTime,
        until: audioTrack.relativeEndTime,
      },
    });
  }

  /**
   * @return {PlayList.BGMusicTrackItem|null}
   */
  createPlayerBGMusicTrack() {
    const audio = app.rootStore.state.story.storyBackgroundMusic;

    if (!_.isEmpty(audio)) {
      const pTrack = {
        src: app.prependHttp(audio.hiResAssetUrl),
      };

      if (!_.isEmpty(pTrack.src)) {
        const duration = audio.duration;
        if (duration) {
          pTrack.play = {
            from: 0,
            duration: duration,
            until: duration,
          };
        }

        return new RCP.ns.PlayList.BGMusicTrackItem(pTrack);
      }
    }

    return null;
  }

  /**
   * Create playlist visual track for sneakPeek purposes
   * @note that because of the nature of sneakPeek only fragment's original video is supported (no image no audio)
   * @param vTrack
   * @return {PlayList.VisualTrackItem}
   */
  static createSneakPeekVisualTrack(vTrack) {
    let src = vTrack.metadata.urlCdn;

    // social fragment has anomaly
    if (app.assetType.isSOCIALFRAGMENT(vTrack)) {
      src = vTrack.metadata.url;
    }

    const pTrack = {
      type: 'video',
      src,
    };

    return new RCP.ns.PlayList.VisualTrackItem(pTrack);
  }
  /**
   * @typedef {Object} CreatePlayerVisualTrackOptions
   * @property {Object} asset the asset from the server
   * @property {Boolean} ignoreRtp ignoring the rtp data and using the client AGD (a.k.a. cropping)
   * @property {Object} layout the layout of the asset ({top:Number, left:Number, bottom: Number, bottom: Number}).
   * @property {Boolean} addStartTime add the start time of the layout to to each of the the overlay start time.
   */
  /**
   * @param options {CreatePlayerVisualTrackOptions}
   * @return {PlayList.VisualTrackItem}
   */
  static createPlayerVisualTrack({
    asset,
    ignoreRtp = false,
    layout = null,
    addStartTime = true,
    ignoreCropping = false,
    unique = false,
    ignoreOverlay = false,
  }) {
    const ignorePRD =
      app.assetType.isSOCIALFRAGMENT(asset) || app.assetType.isSOCIAL(asset);
    const pTrack = {
      volume: null,
      src: asset.metadata.urlCdn || asset.metadata.url,
      play: {
        from: asset.displayStartTime,
        duration: asset.displayDuration,
        until: asset.displayEndTime,
      },
      origin: null,
    };

    if (
      !ignoreRtp &&
      app.realTimePreviewController.isInProgress(
        asset,
        app.RealtimePreviewType.VIDEO
      )
    ) {
      pTrack.effect = RCP.ns.AfterEffectsProcessor.EFFECT_TYPE.RTP_IN_PROGRESS;
    }

    pTrack.volumeControl = asset.volumeControl;
    pTrack.onVoPlayVolumeControl = asset.onVoPlayVolumeControl;

    if (!ignoreRtp && asset.realTimePreviewResultData) {
      pTrack.origin = 'RTP';
      pTrack.type = 'video';
      pTrack.blob = true;
      pTrack.src = app.prependHttp(asset.realTimePreviewResultData.cdnUrl);
      pTrack.fragment = {
        from: 0,
        duration: pTrack.play.duration,
        until: pTrack.play.duration,
      };
    } else if (!ignorePRD && asset.previewResultData) {
      pTrack.origin = 'PRD';
      pTrack.type = 'video';
      pTrack.blob = true;
      pTrack.src = app.prependHttp(asset.previewResultData.cdnUrl);
      pTrack.fragment = {
        from: asset.previewResultData.startTime,
        duration:
          asset.previewResultData.endTime - asset.previewResultData.startTime,
        until: asset.previewResultData.endTime,
      };
    } else {
      if (app.assetType.isFRAGMENT(asset) || app.assetType.isGIF(asset)) {
        if (app.assetType.isGIF(asset)) {
          pTrack.loop = true;
          pTrack.pingPung = asset.agd.pingPongGif;
        }
        pTrack.type = 'video';
        pTrack.blob = true;
        pTrack.fragment = {
          from: asset.startTime,
          duration: asset.endTime - asset.startTime,
          until: asset.endTime,
        };
      } else if (app.assetType.isTWITTER(asset) && !pTrack.src) {
        pTrack.type = 'image';
        pTrack.src = app.TWITTER_PLACEHOLDER.URL_BIG;
      } else if (app.assetType.isFACEBOOK(asset) && !pTrack.src) {
        pTrack.type = 'image';
        pTrack.src = app.FACEBOOK_PLACEHOLDER.URL_BIG;
      } else if (app.assetType.isWEBPAGE(asset)) {
        pTrack.type = 'image';
        pTrack.fitToEdge = 'width';
      } else if (app.assetType.isWALLPAPER(asset)) {
        pTrack.type = 'wallpaper';
        pTrack.cssStyle = app.cssObjectToString(
          app.computeBgFromColorData(asset.wallpaperMetaData.wallpaperData)
        );
      } else {
        pTrack.type = 'image';
      }
    }

    const canCrop = !ignoreCropping && (ignoreRtp || app.rcpShouldCrop(asset));

    if (canCrop && asset.agd) {
      pTrack.cropValues = {
        scale: asset.agd.scale,
        offset: asset.agd.offset,
      };
    }

    const overlayTracks = ignoreOverlay
      ? []
      : (ignoreRtp && asset.overlayTracks) || [];
    pTrack.overlayTracks = overlayTracks.map((ol) => {
      const olPTrack = this.createPlayerVisualTrack({
        asset: ol,
        ignoreRtp,
        ignorePRD,
        unique: true,
      });
      if (canCrop && ol.sceneAgd) {
        olPTrack.sceneCropValues = {
          scale: ol.sceneAgd.scale,
          offset: ol.sceneAgd.offset,
        };
      }
      olPTrack.layouts = {
        mainTrackBoundingBox: app.getMainLayout(ol.sceneLayout),
        overlayAssetBoundingBox: app.getOverlayLayout(ol.sceneLayout),
      };
      if (addStartTime) {
        olPTrack.play.from += pTrack.play.from;
        olPTrack.play.until += pTrack.play.from;
      }
      return olPTrack;
    });

    if (layout) {
      pTrack.layout = layout;
    }

    return new RCP.ns.PlayList.VisualTrackItem(pTrack, unique);
  }

  static getGifLoopsDuration(asset, options) {
    options = $.extend(
      {
        loopsNumber: 2,
        maxTimeForLooping: 20,
      },
      options
    );

    const assetDuration = asset.metadata.duration;
    const loopsDuration = assetDuration * options.loopsNumber;

    return loopsDuration > options.maxTimeForLooping
      ? assetDuration
      : loopsDuration;
  }

  getTrackLimits(asset) {
    const limit = {};

    if (app.assetType.isFRAGMENT(asset)) {
      // fragment start|end time limits, relative to original video
      limit.fragment = {
        min: app.DEFAULT.videoDurationPadding,
        max: fg.trunc(
          asset.metadata.duration - app.DEFAULT.videoDurationPadding
        ),
      };
      // fragment duration limits
      limit.min = fg.trunc(this.story.get('minSelectableVideoSegmentTime'));
      limit.max = fg.trunc(limit.fragment.max - limit.fragment.min);
      limit.initial = fg.trunc(asset.endTime - asset.startTime);
    } else if (
      app.assetType.isINFOGRX(asset) ||
      app.assetType.isSOCIAL(asset) ||
      app.assetType.isSOCIALFRAGMENT(asset)
    ) {
      limit.min = fg.trunc(this.programmer.get('configMinInfoGfxDT'));
      limit.max = fg.trunc(this.programmer.get('configMaxInfoGfxDT'));
      limit.initial = fg.trunc(this.programmer.get('configInitialInfoGfxDT'));
    } else if (app.assetType.isGIF(asset)) {
      limit.min = fg.trunc(this.programmer.get('configMinImageDT'));
      limit.max = fg.trunc(this.programmer.get('configMaxImageDT'));
      limit.initial = TimelineGatewayController.getGifLoopsDuration(asset);
    } else if (app.assetType.isIMAGE(asset)) {
      limit.min = fg.trunc(this.programmer.get('configMinImageDT'));
      limit.max = fg.trunc(this.programmer.get('configMaxImageDT'));
      limit.initial = this.programmer.get('configInitialImageDT');
    } else if (app.assetType.isWALLPAPER(asset)) {
      limit.min = fg.trunc(this.programmer.get('configMinImageDT'));
      limit.max = fg.trunc(this.programmer.get('configMaxImageDT'));
      limit.initial = fg.trunc(this.programmer.get('configInitialImageDT'));
    }

    // validate
    if (
      !_.isNumber(limit.min) ||
      !_.isNumber(limit.initial) ||
      !_.isNumber(limit.max)
    ) {
      app.doctorController.report(
        `TimelineGatewayController::getTrackLimits -> undefined timeline limits for tTrack "${JSON.stringify(
          limit
        )}"`
      );
    } else if (limit.initial < limit.min) {
      app.doctorController.report(
        'TimelineGatewayController::getTrackLimits -> initial limit is less then minimum'
      );
      // autocorrect
      limit.initial = limit.min;
    }

    return limit;
  }

  /**
   * Find time-gaps in video track
   * @param [aTracks] {Array.<PlayList.TrackItem>}
   * @return {PlaylistGapDescriptor}
   */
  storyFindGaps(aTracks) {
    const gd = new PlaylistGapDescriptor();
    const totalDisplayTime = app.rootStore.state.story.timelineDuration;

    if (!_.isDefined(aTracks)) {
      aTracks = this.getStoryVisualTracks();
    }

    if (_.isEmpty(aTracks)) {
      // whole timeline is empty
      gd.aGap.push(
        new GapItem({
          index: 0,
          displayStartTime: 0,
          displayDuration: totalDisplayTime,
          displayEndTime: totalDisplayTime,
        })
      );
      gd.totalGapTime = totalDisplayTime;
      gd.isTimelineEmpty = true;
    } else {
      let currentTrack = aTracks[0];
      let prevTrack = null;
      let time;

      // check gap time before first asset
      time = Math.abs(currentTrack.displayStartTime);
      gd.totalGapTime += time;

      if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
        gd.aGap.push(
          new GapItem({
            index: 0,
            displayStartTime: 0,
            displayDuration: time,
            displayEndTime: time,
          })
        );
      }

      // check for gaps between assets
      for (let i = 1, I = aTracks.length; i < I; i++) {
        currentTrack = aTracks[i];
        prevTrack = aTracks[i - 1];

        time = Math.abs(
          currentTrack.displayStartTime - prevTrack.displayEndTime
        );
        gd.totalGapTime += time;

        if (currentTrack.isSOTPlaceholder) {
          gd.aGap.push(
            new GapItem({
              index: i,
              displayStartTime: currentTrack.displayStartTime,
              displayDuration: currentTrack.displayDuration,
              displayEndTime: currentTrack.displayEndTime,
            })
          );
          gd.totalGapTime += currentTrack.displayDuration;
        }

        if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
          gd.aGap.push(
            new GapItem({
              index: i,
              displayStartTime: prevTrack.displayEndTime,
              displayDuration: time,
              displayEndTime: currentTrack.displayStartTime,
            })
          );
        }
      }

      // check gap time after last asset
      if (currentTrack && totalDisplayTime > currentTrack.displayEndTime) {
        time = Math.abs(totalDisplayTime - currentTrack.displayEndTime);
        gd.totalGapTime += time;

        if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
          gd.aGap.push(
            new GapItem({
              index: aTracks.length,
              displayStartTime: currentTrack.displayEndTime,
              displayDuration: time,
              displayEndTime: totalDisplayTime,
            })
          );
        }
      }
    }

    gd.hasGaps = gd.aGap.length > 0;

    return gd;
  }

  widgetToStory() {
    const timelineAssets = this.getVisualData();
    const timelineData = this.story.getTimelineData();

    timelineData.visualTracks =
      app.namespace.Controller.AssetController.serialize(timelineAssets);
    timelineData.totalDisplayTime =
      app.rootStore.getters['timemachine/maxLayerDuration'];
    app.rootStore.commit(
      'setAudioTracks',
      app.rootStore.getters['timemachine/serializedAudioTracks']
    );

    app.rootStore.dispatch('RECALCULATE_FINAL_STORY_DURATION');

    _.defer(() => {
      this.updateUsageMarkers(timelineAssets);
    });

    this.whenWidgetReady.resolve();
  }

  getVisualData() {
    return app.rootStore.getters['timemachine/visualData'];
  }

  getAudioData() {
    return app.rootStore.getters['timemachine/serializedAudioTracks'];
  }

  getShuffleTransitions() {
    const transitionName = this.story.get('transitionSelection').selectedPreset;
    const transitionsPreset =
      this.story.getTransitionPresetByName(transitionName);
    if (!transitionsPreset) {
      throw new Error('no transition group found');
    }
    const pool = transitionsPreset.transitions.map((t) => t.wochletName);
    const visualTracks = _.cloneDeepAndClean(this.getVisualData());
    return visualTracks.map(({ agd, guid }) => {
      if (agd.transition && agd.transition.selectedByUser) {
        return agd;
      } else {
        return $.extend(
          { agd, guid },
          {
            agd: {
              transition: {
                wiperType: 'WIPER',
                presetName: transitionName,
                wiperName: pool[Math.floor(Math.random() * pool.length)],
              },
            },
          }
        );
      }
    });
  }

  applyTransitionsWithShuffle() {
    const currentVisualTracks = this.getVisualData();
    const visualTracksCopy = this.getShuffleTransitions();

    this.applyTransitions(currentVisualTracks, visualTracksCopy);
  }

  applyTransitionsWithRemove() {
    const currentVisualTracks = this.getVisualData();
    const visualTracksCopy = _.cloneDeepAndClean(currentVisualTracks).map(
      ({ agd, guid }) => {
        if (agd.transition && agd.transition.selectedByUser) {
          return agd;
        }
        return $.extend(
          { agd, guid },
          {
            agd: {
              transition: {
                wiperType: null,
                wiperName: null,
                presetName: null,
              },
            },
          }
        );
      }
    );

    this.applyTransitions(currentVisualTracks, visualTracksCopy);
  }

  applyTransitions(current, modified) {
    _.each(current, ({ agd, guid }) => {
      const vt = modified.find((vt) => vt.guid === guid);
      if (vt) {
        const transition = vt.agd.transition;
        if (!transition || transition.selectedByUser) {
          return;
        }
        $.extend(agd, {
          transition,
        });
      }
    });

    this.widgetToStory();
    this.undoredo.append();
    this.story.hasBeenChanged();
  }

  /**
   * loop through existing visualtracks
   * and update the ones that need preview - automatically
   */
  // zendive.timelineRedesign _initRtpLookup
  initRtpLookup(options = { resetRTP: true }) {
    const assets = this.getVisualData();
    _.each(assets, (asset) => {
      if (!asset.realTimePreviewResultData) {
        this.decideRealTimePreview(
          Object.assign(options, {
            visualTrack: asset,
            delay: 0,
          })
        );
      }
    });
  }

  refreshRtpLookup(options = { resetRTP: true, resetUndoRedo: false }) {
    const assets = this.getVisualData();
    _.each(assets, (asset) => {
      this.decideRealTimePreview(
        Object.assign(options, {
          visualTrack: asset,
          delay: 0,
        })
      );
    });
    if (options.resetUndoRedo) {
      this.resetUndoRedoState();
    }
  }

  // zendive.timelineRedesign
  removeUnsupportedAGDForScene() {
    const assets = this.getVisualData();

    _.each(assets, (asset) => {
      if (
        !asset ||
        !asset.agd ||
        !asset.agd.pingPongGif ||
        !asset.overlayTracks ||
        !asset.overlayTracks.length
      ) {
        return;
      }

      asset.agd.pingPongGif = false;

      storyViewBus.$emit('svbTimelineRedrawAsset', asset);

      this.decideRealTimePreview({
        visualTrack: asset,
        delay: 0,
      });
    });
  }

  // zendive.timelineRedesign _decideRealTimePreview
  decideRealTimePreview(options) {
    options = Object.assign(
      {
        visualTrack: null,
        resetRTP: true,
        delay: 0,
      },
      options
    );

    const visualTrack = options.visualTrack;
    const checksum = app.generateAssetRealTimeChecksum({
      visualTrack,
      aspectRatio: this.story.get('aspectRatio'),
      themeOverrides: app.rootStore.getters.themeOverrides,
    });

    if (options.resetRTP) {
      app.resetVisualTrackRTP(visualTrack);
    } else if (visualTrack._realTimePreviewInProgress) {
      if (visualTrack._tmpRealTimePreviewChecksum === checksum) {
        const rtp = app.realTimePreviewController.getPreviewModelByChecksum(
          visualTrack._tmpRealTimePreviewChecksum
        );
        if (rtp) {
          app.realTimePreviewController.increaseRtpHits(
            visualTrack._tmpRealTimePreviewChecksum
          );
          rtp.model.set('_lastTouch', Date.now());
          return;
        }
      } else {
        app.resetVisualTrackRTP(visualTrack);
      }
    } else {
      if (visualTrack.realTimePreviewChecksum === checksum) {
        return;
      } else {
        app.resetVisualTrackRTP(visualTrack);
      }
    }

    const rtpPromise = app.realTimePreviewController.addTrackToPreviewQueue({
      delay: options.delay,
      visualTrack: visualTrack,
      source: 'timeline',
    });

    rtpPromise.then((rtpModel) => {
      if (rtpModel) {
        visualTrack._tmpRealTimePreviewChecksum = rtpModel.get(
          'realTimePreviewChecksum'
        );
        rtpModel.set('_lastTouch', Date.now());
        storyViewBus.$emit('svbTimelineRtp', { rtpModel, update: false });
      }

      this.serializeTimelineDebounced();
    }, _.noop);
  }

  /**
   * Something has changed in timeline:
   * update story and instant video player
   * @param data {object}
   * @param [options] {object}
   */
  // zendive.timelineRedesign updateSequence
  serializeTimeline(data, options) {
    data = Object.assign(
      {
        e: null,
        type: null,
        seekToTime: null,
        skipHistory: false,
      },
      data
    );
    options = Object.assign(
      {
        pause: true,
      },
      options
    );

    this.widgetToStory();

    app.rootStore.dispatch('RECALCULATE_FINAL_STORY_DURATION');

    const feed = this.storyToPlayer();

    storyViewBus.$emit('svbUpdateRcp', {
      feed: feed,
      pause: options.pause,
      seekToTime: data.seekToTime,
    });

    // remember changes only when they are made by user interaction (has event)
    if (data.e && !options.skipHistory) {
      this.undoredo.append();
    }

    this.story.invalidateFullPreview();
    this.story.hasBeenChanged();
  }

  // zendive.timelineRedesign VM::_updateUsageMarkers
  updateUsageMarkers(visualTracks, audioTracks) {
    visualTracks = visualTracks || this.getVisualData();
    audioTracks = audioTracks || this.getAudioData();
    _.each(app.rootStore.getters.selectedAssets, (asset) => {
      let inUse = _.some(visualTracks, (visualTrack) => {
        return (
          visualTrack.id === asset.id ||
          visualTrack.metadata?.assetUUID === asset.metadata?.assetUUID
        );
      });
      if (!inUse) {
        // if not as scene, maybe its used as overlay or as custom overlay
        inUse = _.some(visualTracks, (scene) => {
          const usedAsCustomOverlay =
            PictureInPictureAdapter.hasThisCustomOverlay(scene, asset);
          const usedAsSceneOverlay = PictureInPictureAdapter.hasThisOverlay(
            scene,
            asset
          );
          return usedAsCustomOverlay || usedAsSceneOverlay;
        });
      }
      app.Vue.set(asset, '_isInTimeline', inUse);
    });
    _.each(app.rootStore.state.story.selectedVoiceOverSegments, (segment) => {
      const inUse = _.some(audioTracks, {
        assetUUID: segment.audioSegmentContent.asset.metadata.assetUUID,
      });
      app.Vue.set(segment, '_isInTimeline', inUse);
    });
  }

  // zendive.timelineRedesign VM::_fragmentDurationChanged
  fragmentDurationChanged(vt) {
    // preview becomes irrelevant when changing start/end times
    if (app.assetType.isFRAGMENT(vt)) {
      vt.previewResultData = null;
      console.info(
        '_fragmentDurationChanged',
        app.sfp(vt.endTime - vt.startTime)
      );
    }
  }

  timelineAddedVisualAsset({ external, asset, replacedAsset }) {
    app.setDefaultAssetVolumes({
      asset,
      story: this.story,
      replacedAsset,
    });

    app.setDefaultAssetItemSchemaModel({
      asset,
      replacedAsset,
      resetTimelineItemIds: true,
    });

    if (external) {
      if (replacedAsset) {
        app.resetVisualTrackRTP(asset);
      }

      app.setDefaultAGD(this.story, asset, replacedAsset);
      app.rootStore.dispatch('POST_INSERT_TRANSCRIPT_CHECK', { asset });

      if (replacedAsset) {
        PictureInPictureAdapter.sceneReplace({
          oldScene: replacedAsset,
          newScene: asset,
        });

        app.analytics('analytics:timeline:replaceAsset', {
          originalAsset: replacedAsset.type,
          newAsset: asset.type,
          sameType: replacedAsset.type === asset.type,
          hasAudioOnTimeline: app.rootStore.getters.hasAudioOnTimeline,
        });
      }

      // zendive.timelineRedesign.phase2 - should be obsolete
      // storyViewBus.$emit('svbTimelineRedrawAsset', asset);
    }
  }

  applyVolumeToAll(asset) {
    const { volumeControl, onVoPlayVolumeControl, guid: assetGuid } = asset;
    const hasValidAudio = !!asset.hasValidAudio;
    // Keep values for later usage on stash
    const stashedVolumeKey = app.generateAssetStashedVolumeKey(
      asset,
      this.story.get('id'),
      'novo'
    );
    app.storyController.story.remoteStash.set(stashedVolumeKey, {
      video: volumeControl.video,
      bg: volumeControl.bgMusic,
    });

    const stashedVolumeKeyVo = app.generateAssetStashedVolumeKey(
      asset,
      this.story.get('id'),
      'vo'
    );
    app.storyController.story.remoteStash.set(stashedVolumeKeyVo, {
      video: onVoPlayVolumeControl.video,
      bg: onVoPlayVolumeControl.bgMusic,
      narration: onVoPlayVolumeControl.narration,
    });

    // set volume on all other assets on timeline
    const assets = this.getVisualData();
    _.each(assets, (asset) => {
      const tmpAssetHasValidAudio = !!asset.hasValidAudio;
      if (assetGuid !== asset.guid && tmpAssetHasValidAudio === hasValidAudio) {
        asset.volumeControl.video = volumeControl.video;
        asset.volumeControl.bgMusic = volumeControl.bgMusic;
        asset.onVoPlayVolumeControl.video = onVoPlayVolumeControl.video;
        asset.onVoPlayVolumeControl.bgMusic = onVoPlayVolumeControl.bgMusic;
        asset.onVoPlayVolumeControl.narration = onVoPlayVolumeControl.narration;
      }
    });

    this.serializeTimelineDebounced();
  }

  translateTimeline(translations) {
    const translatedTracks = TimelineTranslateController.translate({
      translations,
      assets: this.getVisualData(),
    });

    if (!_.isEmpty(translatedTracks)) {
      _.each(translatedTracks, (track) => {
        storyViewBus.$emit('svbTimelineRedrawAsset', track);

        this.decideRealTimePreview({
          visualTrack: track,
          delay: 0,
        });
      });

      this.serializeTimelineDebounced();
    }
  }

  handleAssetSplit({ originalAsset, cloneAsset, assetSplitTimes }) {
    TextOverlayEffectController.handleAssetSplit({
      originalAsset,
      cloneAsset,
      assetSplitTimes,
    });

    this.decideRealTimePreview({
      visualTrack: originalAsset,
      delay: app.TIME.SECOND,
    });

    this.decideRealTimePreview({
      visualTrack: cloneAsset,
      delay: app.TIME.SECOND,
    });

    // update the video poster with the new original asset data
    if (app.storyController.isVideoPoster(originalAsset)) {
      this.story.setVideoPosterAuto(originalAsset);
    }
  }

  // _onWallpaperReplaced
  handleWallpaperReplace({ newWallpaper, oldId, oldName }) {
    const oldIsBrandColor = !_.isEmpty(oldName);
    let prop;
    let value;
    if (oldIsBrandColor) {
      prop = 'colorName';
      value = oldName;
    } else {
      prop = 'wallpaperId';
      value = oldId;
    }

    const assets = this.getVisualData();
    const isWallpaperToReplace = function (asset) {
      return asset.metadata.assetType === 'WALLPAPER' && asset[prop] === value;
    };

    _.each(assets, (asset) => {
      let needRedraw = false;

      if (isWallpaperToReplace(asset)) {
        this._replaceWallpaper(asset, newWallpaper);
        needRedraw = true;
      }

      _.each(asset[PictureInPictureAdapter.TRACK_PROP], (overlay) => {
        if (isWallpaperToReplace(overlay)) {
          this._replaceWallpaper(overlay, newWallpaper);
          needRedraw = true;
        }
      });

      if (needRedraw) {
        storyViewBus.$emit('svbTimelineRedrawAsset', asset);

        this.decideRealTimePreview({
          visualTrack: asset,
          delay: 0,
        });
      }
    });

    this.serializeTimelineDebounced();
    this.undoredo.append();
  }

  _replaceWallpaper(asset, newWallpaper) {
    if (newWallpaper.name) {
      asset.colorName = newWallpaper.name;
      delete asset.wallpaperId;
    } else if (newWallpaper.id) {
      asset.wallpaperId = newWallpaper.id;
      delete asset.colorName;
    }

    asset.wallpaperMetaData.wallpaperData = newWallpaper.wallpaperData;
    asset.wallpaperMetaData.type = newWallpaper.wallpaperType;
    asset.cssStyleObj = app.computeBgFromColorData(newWallpaper.wallpaperData);
  }

  afterAspectRatioChanged() {
    const assets = this.getVisualData();
    this.assignModalTimelineAssetsToAssets(assets);

    _.each(assets, (asset) => {
      // check if the transition group exist in the incoming aspect ratio
      // if so remove the transition and userlock
      if (asset.agd.transition) {
        const noTransition = !_.find(
          this.story.getThemeInfo().transitionPresets,
          {
            name: asset.agd.transition.presetName,
          }
        );
        if (noTransition) {
          asset.agd = $.extend(asset.agd, {
            transition: {
              wiperType: null,
              wiperName: null,
              presetName: null,
            },
          });
        }
      }

      (asset.overlayTracks || []).forEach((ol) => {
        const CATEGORY_LAYOUT_MAP = this._defaultCategoryLayoutsMapper(
          this.story.getThemeInfo().sceneLayouts
        );
        ol.sceneLayout = _.cloneDeepAndClean(
          CATEGORY_LAYOUT_MAP[ol.sceneLayout.category] ||
            CATEGORY_LAYOUT_MAP.default
        );
        /**
         * adjustment the overlay assets not suppose to be here.
         *
         * the right place is in `AspectRatiosController.js` but
         * when i try to put it there i got an error in the timeline...
         *
         * therefore i put it here in a future thinking that it should be
         * in the `AspectRatiosController.js`
         */
        const AdjustOverlay = RatioChange_AutoCrop();
        const { scale: overlayScale, offset: overlayOffset } =
          AdjustOverlay.AdjustAssetPositionBase({
            assetPosition: app.getOverlayLayout(ol.sceneLayout),
            baseAspectRatio: {
              width: app.ASSET_EDITOR_WIDTH,
              height: app.ASSET_EDITOR_HEIGHT,
            },
            asset: ol,
          });
        ol.agd.scale = overlayScale;
        ol.agd.offset = overlayOffset;
        const AdjustMain = RatioChange_AutoCrop();
        const { scale: mainScale, offset: mainOffset } =
          AdjustMain.AdjustAssetPositionBase({
            assetPosition: app.getMainLayout(ol.sceneLayout),
            baseAspectRatio: {
              width: app.ASSET_EDITOR_WIDTH,
              height: app.ASSET_EDITOR_HEIGHT,
            },
            asset,
          });
        ol.sceneAgd = {
          scale: mainScale,
          offset: mainOffset,
        };
      });

      PictureInPictureAdapter.ensureAnimationCutEx({ scene: asset });
      this.decideRealTimePreview({
        visualTrack: asset,
        delay: 0,
      });

      storyViewBus.$emit('svbTimelineRedrawAsset', asset);
    });

    // clearing defaults when AR change.
    app.setSceneLayoutObject(void 0);

    this.serializeTimelineDebounced();
    this.resetUndoRedoState();
  }

  resetUndoRedoState() {
    if (!this.undoredo) {
      return;
    }
    this.undoredo.reset();
  }

  /**
   * map the id of the name default category to his layout.
   *
   * @param {Object} sceneLayouts - the pure sceneLayout property from the theme info.
   * @return {Object} key: category name, value: the default layout of the category.
   */
  _defaultCategoryLayoutsMapper(sceneLayouts) {
    // get default layout values
    const DEFAULT_LAYOUT = Object.values(sceneLayouts.defaultLayoutMap);

    // create an empty object for the categories map.
    const CATEGORY_LAYOUT_MAP = {};

    // flatten all of the layouts
    const allLayouts = _.flattenDeep(
      sceneLayouts.layoutCategories.map((sl) =>
        sl.sceneWindows.map((l) => l.layouts)
      )
    ).filter((l) => ~DEFAULT_LAYOUT.indexOf(l.id));

    // set default category.
    CATEGORY_LAYOUT_MAP.default = allLayouts.find((l) => {
      return (
        l.id === sceneLayouts.defaultLayoutMap[sceneLayouts.defaultCategory]
      );
    });

    // assign the category to layout key value.
    allLayouts.forEach((l) => {
      CATEGORY_LAYOUT_MAP[l.category] = _.cloneDeep(l);
    });

    // return the category layout map.
    return CATEGORY_LAYOUT_MAP;
  }

  /**
   * WTF MOMENT!!!
   * this is a HUCK!
   *
   * the `executeTimelineFunction('getAssets')` function isn't getting
   * the agd from the `this.model.getTimelineData`. this is a problem!
   *
   * changes on the AGD that done in `AspectRatioController` done on the
   * reference of the asset object in `this.model.getTimelineData`
   *
   * (assets)-> timeline.$el.data()
   * (this.model.getTimelineData) -> StoryModel.timelineData
   *
   * this function assign the agd that change via the `AspectRatioController`
   * and apply it on the assets that gets from the `executeTimelineFunction('getAssets')`.
   *
   * the link below is a suggestment of how to solve this issue
   * @link https://wochit.atlassian.net/browse/DEV-9662
   */
  assignModalTimelineAssetsToAssets(assets) {
    function getAssetKey(asset) {
      return `${asset.id}_${asset.displayStartTime}`;
    }

    // map the asset base on getAssetKey
    const assetLocationMap = assets.reduce(
      (obj, asset, i) => ({
        ...obj,
        [getAssetKey(asset)]: i,
      }),
      {}
    );

    // assign the visualTracks assets base on the assetLocationMap
    this.story
      .getTimelineData()
      .visualTracks.forEach(
        (asset) =>
          (assets[assetLocationMap[getAssetKey(asset)]].agd = asset.agd)
      );
  }

  _assetMetadataChanged(
    updateDetails = {
      assetUUID: null,
      metadata: {},
    }
  ) {
    this.whenWidgetReady.then(() => {
      const assets = this.getVisualData();
      const relevant = _.filter(assets, (asset) => {
        return asset.metadata.assetUUID === updateDetails.assetUUID;
      });

      _.each(relevant, (asset) => {
        Object.assign(asset.metadata, updateDetails.metadata);
        storyViewBus.$emit('svbTimelineRedrawAsset', asset);
      });

      this.serializeTimelineDebounced();
    });
  }
}
